import {ExternalLink, FileCheck2, FileWarning, UserCheck} from "lucide-react";
import {formatDateTime} from "../../../shared/utils/format";
import {useTranslation, i18n} from "../../../shared/i18n";


const isSafeHttpsUrl = (value) => {
    if (typeof value !== "string") return false;
    try {
        return new URL(value).protocol === "https:";
    } catch (_error) {
        return false;
    }
};

const MissingValue = () => (
    <span className="onboarding-missing-value">
        <span aria-hidden="true">—</span>
        <span>{i18n.t("sections.onboarding.p27")}</span>
    </span>
);

const DataRow = ({label, value, children}) => (
    <div className="onboarding-data-row">
        <dt>{label}</dt>
        <dd>{children || (value ? value : <MissingValue />)}</dd>
    </div>
);

const ExternalValue = ({label, value}) => {
    const {t} = useTranslation();
    if (!value) return <MissingValue />;
    if (!isSafeHttpsUrl(value)) return <span className="onboarding-raw-value">{value}</span>;
    return (
        <a href={value} target="_blank" rel="noopener noreferrer">
            {label}
            <ExternalLink aria-hidden="true" size={13} />
            <span className="onboarding-sr-only"> {t("sections.onboarding.p70")}</span>
        </a>
    );
};

const SummarySection = ({id, title, children}) => (
    <section className="onboarding-summary-section" aria-labelledby={id}>
        <h2 id={id}>{title}</h2>
        <dl className="onboarding-detail-grid">{children}</dl>
    </section>
);

export const ApplicationSummary = ({application}) => (
    <div className="onboarding-summary" data-testid="application-summary">
        <section className="onboarding-evidence-strip" aria-label={i18n.t("sections.onboarding.p71")}>
            <div>
                <UserCheck aria-hidden="true" size={17} />
                <span>
                    <strong>{i18n.t("sections.onboarding.p72")} {application.revision}</strong>
                    <small>
                        {application.moderated_by
                            ? `${application.moderated_by.name} · ${formatDateTime(application.moderated_at)}`
                            : i18n.t("sections.onboarding.p73")}
                    </small>
                </span>
            </div>
            <div>
                {application.rejected_documents_count > 0 ? (
                    <FileWarning aria-hidden="true" size={17} />
                ) : (
                    <FileCheck2 aria-hidden="true" size={17} />
                )}
                <span>
                    <strong>{application.documents_count} {i18n.t("sections.onboarding.p74")}</strong>
                    <small>{application.rejected_documents_count} {i18n.t("sections.onboarding.p53")}</small>
                </span>
            </div>
            <div className="onboarding-evidence-comment">
                <span>{i18n.t("sections.onboarding.p75")}</span>
                <strong>{application.moderation_comment || i18n.t("sections.onboarding.p76")}</strong>
            </div>
        </section>

        <SummarySection id="onboarding-applicant-title" title={i18n.t("sections.onboarding.p58")}>
            <DataRow label={i18n.t("sections.onboarding.p77")} value={[application.first_name, application.last_name].filter(Boolean).join(" ")} />
            <DataRow label={i18n.t("sections.onboarding.p78")} value={application.applicant_name} />
            <DataRow label={i18n.t("sections.onboarding.p79")} value={application.phone_number} />
            <DataRow label="Email" value={application.email} />
        </SummarySection>

        <SummarySection id="onboarding-legal-title" title={i18n.t("sections.onboarding.p80")}>
            <DataRow label={i18n.t("sections.onboarding.p81")} value={application.company_name} />
            <DataRow label={i18n.t("sections.onboarding.p82")} value={application.company_type} />
            <DataRow label={i18n.t("sections.onboarding.p83")} value={application.bin_number} />
            <DataRow label={i18n.t("sections.onboarding.p84")} value={application.director_full_name} />
            <DataRow label={i18n.t("sections.onboarding.p85")} value={application.legal_address} />
            <DataRow label={i18n.t("sections.onboarding.p86")} value={application.actual_address} />
        </SummarySection>

        <SummarySection id="onboarding-business-card-title" title={i18n.t("sections.onboarding.p87")}>
            <DataRow label={i18n.t("sections.onboarding.p88")} value={application.display_name} />
            <DataRow label={i18n.t("sections.onboarding.p89")} value={application.category_name} />
            <DataRow label={i18n.t("sections.onboarding.p90")} value={application.description} />
            <DataRow label="Instagram">
                <ExternalValue label="Instagram" value={application.instagram} />
            </DataRow>
            <DataRow label="2GIS">
                <ExternalValue label="2GIS" value={application.gis_link} />
            </DataRow>
        </SummarySection>

        <SummarySection id="onboarding-bank-title" title={i18n.t("sections.onboarding.p91")}>
            <DataRow label={i18n.t("sections.onboarding.p92")} value={application.bank_name} />
            <DataRow label={i18n.t("sections.onboarding.p93")} value={application.bik} />
            <DataRow label={i18n.t("sections.onboarding.p94")} value={application.account_number} />
        </SummarySection>
    </div>
);
